import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiSearch } from "react-icons/fi";
import { Card, Button, Modal, FormField, DataTable, StatusBadge, SearchInput } from "../../../components/common";
import useAsync from "../../../hooks/useAsync";
import {
  listRoles, listPermissions, getRolePermissions, grantPermission, revokePermission, createRole, updateRole, checkAccess, DATA_SCOPES,
} from "../../../services/rbacApi";
import { getErrorMessage } from "../../../utils/errors";
import Select from "../../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { listRoles, listPermissions, getRolePermissions, grantPermission, revokePermission, createRole, updateRole, checkAccess };

const scopeLabel = (value) => DATA_SCOPES.find((s) => s.value === value)?.label || value;

export default function RolePermissionsCard() {
  const roles = useAsync(() => api.listRoles(), []);
  const catalog = useAsync(() => api.listPermissions(), []);
  const [roleId, setRoleId] = useState(null);
  const granted = useAsync(() => api.getRolePermissions(roleId), [roleId], { skip: !roleId });
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [probe, setProbe] = useState({ permission: "", employeeId: "", result: null });
  const [busy, setBusy] = useState(false);

  const role = (roles.data || []).find((r) => r.id === roleId);
  const permissions = useMemo(() => {
    const q = filter.toLowerCase();
    return (catalog.data || []).filter((p) => !q || p.code.includes(q) || p.module.includes(q));
  }, [catalog.data, filter]);

  const toggle = async (permission, on) => {
    try {
      if (on) await api.grantPermission(roleId, permission.id, `Granted ${permission.code} from Users & roles`);
      else await api.revokePermission(roleId, permission.id, `Revoked ${permission.code} from Users & roles`);
      toast.success(`${permission.code} ${on ? "granted to" : "revoked from"} ${role.name}.`);
      granted.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const saveRole = async () => {
    setBusy(true);
    try {
      if (editing.id) await api.updateRole(editing.id, editing);
      else await api.createRole(editing);
      toast.success(editing.id ? "Role updated." : "Role created. Grant it permissions next.");
      setEditing(null);
      roles.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runProbe = async () => {
    try {
      const { data } = await api.checkAccess(probe.permission.trim(), probe.employeeId.trim());
      setProbe({ ...probe, result: data.allowed });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <>
      <Card
        title="Roles & permissions"
        subtitle="Each role grants permissions within its data scope. Changes apply on the next request."
        padded={false}
        actions={<Button size="sm" variant="secondary" icon={FiPlus} onClick={() => setEditing({ code: "", name: "", description: "", scope: "TEAM", active: true })}>New role</Button>}
      >
        <DataTable
          dense
          rows={roles.data || []}
          loading={roles.loading && !roles.data}
          onRowClick={(r) => setRoleId(r.id)}
          columns={[
            { key: "name", label: "Role", render: (r) => <span className="cell-stack"><strong>{r.name}</strong><small>{r.code}{r.id === roleId ? " · selected" : ""}</small></span> },
            { key: "scope", label: "Data scope", render: (r) => scopeLabel(r.scope) },
            { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Active" : "Inactive"} /> },
            {
              key: "act", label: "", align: "right",
              render: (r) => (
                <span className="row-actions">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setRoleId(r.id); }}>Permissions</Button>
                  <Button size="sm" variant="ghost" icon={FiEdit2} aria-label={`Edit ${r.name}`} onClick={(e) => { e.stopPropagation(); setEditing({ ...r }); }} />
                </span>
              ),
            },
          ]}
        />
      </Card>

      {role && (
        <Card
          title={`Permissions of ${role.name}`}
          subtitle={role.code === "ADMIN" ? "The recovery ADMIN role keeps all its permissions." : `Scope: ${scopeLabel(role.scope)}`}
          actions={<SearchInput value={filter} onChange={setFilter} placeholder="Filter permissions" />}
          padded={false}
        >
          <DataTable
            dense
            pageSize={15}
            rows={permissions}
            loading={granted.loading && !granted.data}
            columns={[
              {
                key: "on", label: "", width: "44px",
                render: (p) => (
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={(granted.data || []).includes(p.id)}
                      disabled={role.code === "ADMIN" || !p.active}
                      onChange={(e) => toggle(p, e.target.checked)}
                      aria-label={p.code}
                    />
                  </label>
                ),
              },
              { key: "code", label: "Permission", render: (p) => <span className="cell-stack"><strong>{p.code}</strong><small>{p.description}</small></span> },
              { key: "module", label: "Module" },
            ]}
          />
        </Card>
      )}

      <Card title="Check my access" subtitle="Diagnostic: does your own account hold a permission for an employee?">
        <div className="filter-bar">
          <input className="form-control" placeholder="Permission code, e.g. team.performance.read" value={probe.permission} onChange={(e) => setProbe({ ...probe, permission: e.target.value, result: null })} />
          <input className="form-control" placeholder="Employee ID" value={probe.employeeId} onChange={(e) => setProbe({ ...probe, employeeId: e.target.value, result: null })} />
          <Button variant="secondary" icon={FiSearch} disabled={!probe.permission || !probe.employeeId} onClick={runProbe}>Check</Button>
          {probe.result !== null && <StatusBadge status={probe.result ? "Allowed" : "Not allowed"} tone={probe.result ? "success" : "danger"} />}
        </div>
      </Card>

      <Modal
        open={Boolean(editing)}
        size="sm"
        title={editing?.id ? `Edit ${editing.name}` : "New role"}
        onClose={busy ? undefined : () => setEditing(null)}
        footer={<><Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button><Button onClick={saveRole} loading={busy}>Save role</Button></>}
      >
        {editing && (
          <div className="form-stack">
            <FormField label="Code" hint="Upper case letters, digits and _ (cannot change later).">
              <input value={editing.code} disabled={Boolean(editing.id)} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })} />
            </FormField>
            <FormField label="Name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></FormField>
            <FormField label="Description"><textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></FormField>
            <FormField label="Data scope">
              <Select value={editing.scope} onChange={(e) => setEditing({ ...editing, scope: e.target.value })}>
                {DATA_SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </FormField>
            {editing.id && (
              <label className="checkbox">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                <span>Active</span>
              </label>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
